// Strip field: fitting type dropdown or selected pill with Change link.
// Not mounted in BookingStripPanel by default (location-only strip); kept
// for reuse if the three-field strip layout returns.

import { useState } from 'react';
import styles from '../styles/widget.module.css';
import { FITTING_TYPES } from '../data/fittingTypes';
import type { BookingAction, BookingState } from '../state/machine';
import { useClickOutside } from '../hooks/useClickOutside';
import { useOpenClose } from '../hooks/useOpenClose';
import ChevronDown from '../assets/icons/ChevronDown';
import Info from '../assets/icons/Info';
import SimplePopup from './SimplePopup';

interface FittingTypeFieldProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  disabled?: boolean;
  hideSelectedPill?: boolean;
}

export function FittingTypeField({ state, dispatch, disabled = false, hideSelectedPill = false }: FittingTypeFieldProps) {
  const [open, setOpen] = useState(false);
  const { isOpen: isOpenFittingTypeInfo, close: closeFittingTypeInfo, open: openFittingTypeInfo } = useOpenClose()
  const wrapperRef = useClickOutside<HTMLDivElement>(() => {
    setOpen(false);
  }, { enabled: open });

  if (state.selectedFittingType) {
    if (hideSelectedPill) return null;
    return (
      <div className={styles.stripPill}>
        <span className={styles.label}>Fitting Type</span>
        <div className={styles.stripPillValue}>
          <span>{state.selectedFittingType.displayName ?? state.selectedFittingType.name}</span>
          <button
            className={styles.stripPillChange}
            onClick={() => dispatch({ type: 'SELECT_FITTING_TYPE', payload: null })}
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.field} ${disabled ? styles.panelSectionDisabled : ''}`} ref={wrapperRef}>
      <label className={styles.label}>
        Fitting Type
        <button type='button' onClick={openFittingTypeInfo} className={styles.infoPopupTrigger}>
          <Info />
        </button>
      </label>
      <SimplePopup
        children={
          <div>
            <h3>DATA DRIVEN FITTING</h3>
            <p>Every Tour-level session begins with a personal interview to analyze your goals and missed shots. Next, we capture your current club data using TrackMan technology. Finally, our master fitters test optimized head and shaft combinations, visually proving your real-time distance and accuracy gains.</p>
          </div>
        }
        isOpen={isOpenFittingTypeInfo}
        onClosePopup={closeFittingTypeInfo} />
      <button
        className={styles.input}
        style={{ textAlign: 'left', cursor: 'pointer', background: '#fff' }}
        onClick={() => setOpen(!open)}
      >
        <span>Select Fitting Type</span>
        <ChevronDown />
      </button>
      {open && (
        <div className={styles.locationDropdown} style={{ maxWidth: 360 }}>
          <div className={styles.locationDropdownHeader}>
            {FITTING_TYPES.length} Fitting Types to Choose From
          </div>
          {FITTING_TYPES.map((ft) => (
            <div
              key={ft.id}
              className={styles.locationRow}
              onClick={() => {
                dispatch({ type: 'SELECT_FITTING_TYPE', payload: ft });
                setOpen(false);
              }}
            >
              <div className={styles.fittingTypeHeader}>
                <span className={styles.fittingTypeName}>{(ft.displayName ?? ft.name).toUpperCase()}</span>
                {ft.badge && <span className={styles.fittingTypeBadge}>{ft.badge}</span>}
              </div>
              <div className={styles.fittingTypeDescription}>{ft.description}</div>
              <div className={styles.fittingTypeMeta}>
                <span>{ft.durationLabel}</span>
                <span>{ft.pricingLabel}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
